import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SourceBadge } from '@/components/provenance/source-badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useAddablePnrs,
  useAddPnrs,
  type AddablePnr,
  type AddPnrsResult,
} from '../use-add-pnrs';

// "Add PNRs" dialog — manifest PNRs not yet in the case, as a multi-select
// checkbox list (locator primary, masked lead + pax count secondary, an
// "excluded earlier" badge on rows the operator once removed). Confirm posts
// exactly the checked pnrUrns; the response then replaces the list with an
// added/skipped summary so the operator sees what actually landed — a skipped
// row names its reason rather than vanishing silently. Two honest empty
// states: no manifest to add from (no flight, or the manifest is unreachable —
// NEVER a synthetic list) vs. every manifest PNR already in the case.
//
// A PNR the airline did not make — generated data, e.g. a mocked airline
// adapter's PNRs on a demo — says «Sintético» on its row, with the same source
// badge as the intake picker (owner, 2026-10-29: a synthetic PNR is always said
// to be one).

interface AddPnrsDialogProps {
  caseUrn: string;
  onClose: () => void;
}

export function AddPnrsDialog({ caseUrn, onClose }: AddPnrsDialogProps) {
  const { t } = useTranslation('caseDetail');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  // The summary of a completed add, with the locators snapshotted at confirm
  // time — the success invalidation refetches the addable list, so the labels
  // for what was just added/skipped must not depend on the NEW list.
  const [result, setResult] = useState<{
    outcome: AddPnrsResult;
    labels: ReadonlyMap<string, string>;
  } | null>(null);

  const addable = useAddablePnrs(caseUrn, true);
  const add = useAddPnrs(caseUrn);

  const toggle = (pnrUrn: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pnrUrn)) next.delete(pnrUrn);
      else next.add(pnrUrn);
      return next;
    });
  };

  const confirm = async (): Promise<void> => {
    const labels = new Map(
      (addable.data?.pnrs ?? []).map((p) => [p.pnrUrn, p.locator ?? p.pnrUrn]),
    );
    try {
      const outcome = await add.mutateAsync([...selected]);
      setResult({ outcome, labels });
    } catch {
      // Mutation state renders the error inline; the dialog stays open to retry.
    }
  };

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="max-w-md" data-testid="add-pnrs-dialog">
        <DialogHeader>
          <DialogTitle>{t('addPnrs.title')}</DialogTitle>
          <DialogDescription>{t('addPnrs.description')}</DialogDescription>
        </DialogHeader>

        {result ? (
          <ResultSummary outcome={result.outcome} labels={result.labels} />
        ) : addable.isLoading ? (
          <div
            className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground"
            data-testid="add-pnrs-loading"
          >
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            {t('addPnrs.loading')}
          </div>
        ) : addable.isError ? (
          <p className="p-2 text-sm text-destructive" data-testid="add-pnrs-load-error">
            {t('addPnrs.loadError')}
          </p>
        ) : addable.data && !addable.data.manifestAvailable ? (
          <p
            className="rounded-md border border-dashed p-4 text-sm text-muted-foreground"
            data-testid="add-pnrs-no-manifest"
          >
            {t('addPnrs.noManifest')}
          </p>
        ) : addable.data && addable.data.pnrs.length === 0 ? (
          <p
            className="rounded-md border border-dashed p-4 text-sm text-muted-foreground"
            data-testid="add-pnrs-all-in"
          >
            {t('addPnrs.allIn')}
          </p>
        ) : (
          <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1" data-testid="add-pnrs-list">
            {(addable.data?.pnrs ?? []).map((p) => (
              <PnrRow key={p.pnrUrn} pnr={p} checked={selected.has(p.pnrUrn)} onToggle={toggle} />
            ))}
          </div>
        )}

        {add.isError && !result ? (
          <p className="text-xs text-destructive" data-testid="add-pnrs-error">
            {t('addPnrs.failed')}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          {result ? (
            <Button onClick={onClose} data-testid="add-pnrs-done">
              {t('addPnrs.done')}
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose} disabled={add.isPending}>
                {t('common:common.cancel')}
              </Button>
              <Button
                onClick={() => void confirm()}
                disabled={selected.size === 0 || add.isPending}
                data-testid="add-pnrs-confirm"
              >
                {add.isPending ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
                {t('addPnrs.confirm', { count: selected.size })}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PnrRow({
  pnr,
  checked,
  onToggle,
}: {
  pnr: AddablePnr;
  checked: boolean;
  onToggle: (pnrUrn: string) => void;
}) {
  const { t } = useTranslation('caseDetail');
  const label = pnr.locator ?? pnr.pnrUrn;
  return (
    // Native checkbox named for the row (same idiom as the create-disruption
    // flight list): tab reaches it, space toggles, the reader hears the PNR.
    <div
      className="flex items-center gap-3 rounded-md border border-border p-2.5 transition-colors hover:bg-muted/50"
      data-testid={`add-pnr-row-${pnr.pnrUrn}`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={() => onToggle(pnr.pnrUrn)}
        aria-label={t('addPnrs.selectPnr', { pnr: label })}
        className="h-4 w-4 shrink-0 accent-primary"
        data-testid={`add-pnr-check-${pnr.pnrUrn}`}
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="font-mono text-sm font-semibold">{label}</span>
          {pnr.source === 'SYNTHETIC' ? (
            <SourceBadge source="SYNTHETIC" className="shrink-0" testId={`add-pnr-source-${pnr.pnrUrn}`} />
          ) : null}
          {pnr.alreadyExcluded ? (
            <Badge variant="warning" size="sm" data-testid={`add-pnr-excluded-${pnr.pnrUrn}`}>
              {t('addPnrs.excludedEarlier')}
            </Badge>
          ) : null}
        </span>
        <span className="mt-0.5 block text-xs text-muted-foreground">
          {t('addPnrs.pax', { count: pnr.paxCount })}
          {pnr.leadPassenger ? ` · ${pnr.leadPassenger}` : null}
        </span>
      </span>
    </div>
  );
}

function ResultSummary({
  outcome,
  labels,
}: {
  outcome: AddPnrsResult;
  labels: ReadonlyMap<string, string>;
}) {
  const { t } = useTranslation('caseDetail');
  return (
    <div className="space-y-2" data-testid="add-pnrs-result">
      <p className="text-sm font-medium">{t('addPnrs.resultAdded', { count: outcome.added })}</p>
      {outcome.skipped.length > 0 ? (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">
            {t('addPnrs.resultSkipped', { count: outcome.skipped.length })}
          </p>
          <ul className="space-y-0.5 text-xs text-muted-foreground">
            {outcome.skipped.map((s) => (
              <li key={s.pnrUrn} className="flex items-center gap-2">
                <span className="font-mono text-foreground">{labels.get(s.pnrUrn) ?? s.pnrUrn}</span>
                <span>{t(`addPnrs.skipReason.${s.reason}`)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
